/**
 * useFindings Hook
 * Finding list and update operations with React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { createQueryKeys } from './queryKeys';
import type {
  Finding,
  FindingListParams,
  FindingSeverity,
  FindingStatus,
} from '../types/api';

// ============================================================================
// Query Keys
// ============================================================================

const base = createQueryKeys('findings');
export const findingKeys = {
  ...base,
  list: (params: FindingListParams) => [...base.lists(), params] as const,
  byPolicy: (policyId: string) => [...base.all, 'policy', policyId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch paginated list of findings
 */
export function useFindings(params: FindingListParams = {}) {
  return useQuery({
    queryKey: findingKeys.list(params),
    queryFn: () => api.findings.list(params),
  });
}

/**
 * Fetch a single finding by ID
 */
export function useFinding(id: string | undefined) {
  return useQuery({
    queryKey: findingKeys.detail(id!),
    queryFn: () => api.findings.get(id!),
    enabled: Boolean(id),
  });
}

/**
 * Fetch findings for a specific policy
 */
export function useFindingsByPolicy(policyId: string | undefined) {
  return useQuery({
    queryKey: findingKeys.byPolicy(policyId!),
    queryFn: () => api.findings.getByPolicy(policyId!),
    enabled: Boolean(policyId),
  });
}

/**
 * Fetch open findings (for exception queue)
 */
export function useOpenFindings(params: Omit<FindingListParams, 'status'> = {}) {
  return useFindings({ ...params, status: 'open' });
}

/**
 * Create a new finding
 */
export function useCreateFinding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: import('../types/api').CreateFindingRequest) =>
      api.findings.create(data),
    onSuccess: (newFinding) => {
      // Invalidate ALL finding queries to ensure the list refreshes
      queryClient.invalidateQueries({ queryKey: findingKeys.all });
    },
  });
}

/**
 * Update a finding
 */
export function useUpdateFinding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import('../types/api').CreateFindingRequest> }) =>
      api.findings.update(id, data),
    onSuccess: (updatedFinding) => {
      // Update the cache for this specific finding
      queryClient.setQueryData(
        findingKeys.detail(updatedFinding.id),
        updatedFinding
      );
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: findingKeys.lists() });
      // Invalidate policy-specific findings if we know the policy ID
      if (updatedFinding.policyId) {
        queryClient.invalidateQueries({
          queryKey: findingKeys.byPolicy(updatedFinding.policyId),
        });
      }
    },
  });
}

/**
 * Delete a finding (soft delete)
 */
export function useDeleteFinding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.findings.delete(id),
    onSuccess: () => {
      // Invalidate all finding queries since we don't have the finding data anymore
      queryClient.invalidateQueries({ queryKey: findingKeys.all });
    },
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get severity priority for sorting (lower is more critical)
 */
export function getSeverityPriority(severity: FindingSeverity): number {
  const priorities: Record<FindingSeverity, number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
  };
  return priorities[severity];
}

/**
 * Sort findings by severity (most critical first)
 */
export function sortFindingsBySeverity(findings: Finding[]): Finding[] {
  return [...findings].sort(
    (a, b) => getSeverityPriority(a.severity) - getSeverityPriority(b.severity)
  );
}

/**
 * Group findings by severity
 */
export function groupFindingsBySeverity(
  findings: Finding[]
): Record<FindingSeverity, Finding[]> {
  return findings.reduce(
    (groups, finding) => {
      const severity = finding.severity;
      if (!groups[severity]) {
        groups[severity] = [];
      }
      groups[severity].push(finding);
      return groups;
    },
    {} as Record<FindingSeverity, Finding[]>
  );
}

/**
 * Group findings by status
 */
export function groupFindingsByStatus(
  findings: Finding[]
): Record<FindingStatus, Finding[]> {
  return findings.reduce(
    (groups, finding) => {
      const status = finding.status;
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(finding);
      return groups;
    },
    {} as Record<FindingStatus, Finding[]>
  );
}

/**
 * Count findings by severity
 */
export function countFindingsBySeverity(
  findings: Finding[]
): Record<FindingSeverity, number> {
  return findings.reduce(
    (counts, finding) => {
      counts[finding.severity] = (counts[finding.severity] || 0) + 1;
      return counts;
    },
    {} as Record<FindingSeverity, number>
  );
}

/**
 * Get color for severity
 */
export function getSeverityColor(severity: FindingSeverity): string {
  const colors: Record<FindingSeverity, string> = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'green',
  };
  return colors[severity];
}

/**
 * Get display label for severity
 */
export function getSeverityLabel(severity: FindingSeverity): string {
  const labels: Record<FindingSeverity, string> = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };
  return labels[severity];
}

/**
 * Tailwind border-left color classes for severity indicators on mobile cards.
 * Shared across FindingsListPage, ExceptionQueuePage, etc.
 */
export const severityBorderColors: Record<FindingSeverity, string> = {
  critical: 'border-l-red-500',
  high: 'border-l-orange-500',
  medium: 'border-l-amber-500',
  low: 'border-l-blue-500',
};

export default useFindings;
